/**
 * Detail view rendering functions for Student Profile
 */
import React from 'react';
import { Divider } from 'primereact/divider';
import { getGradeDisplayName } from './constants';

/**
 * Render student other details view (Personal Info)
 * @param {Object} studentData - Student data object
 * @returns {JSX.Element}
 */
export const renderStudentOtherDetailsView = (studentData) => {
    return (
        <div className="student-other-details-view">
            <h2>Student Other Details</h2>

            <div className="parent-card">
                <div className="parent-details-grid">
                    <div className="detail-section">
                        <div className="detail-item-inline">
                            <label>Phone:</label>
                            <span>{studentData?.studentMobile || '-'}</span>
                        </div>
                        <div className="detail-item-inline">
                            <label>Email:</label>
                            <span>{studentData?.studentEmail || '-'}</span>
                        </div>
                        <div className="detail-item-inline">
                            <label>Aadhaar No:</label>
                            <span>{studentData?.aadharNo || '-'}</span>
                        </div>
                    </div>

                    <div className="detail-section">
                        <div className="detail-item-inline">
                            <label>Mother tongue:</label>
                            <span>{studentData?.motherTongue?.toUpperCase() || '-'}</span>
                        </div>
                        <div className="detail-item-inline">
                            <label>Caste / Sub Caste:</label>
                            <span>{studentData?.caste?.toUpperCase() || '-'}</span>
                        </div>
                    </div>

                    <div className="detail-section">
                        <div className="detail-item-inline">
                            <label>Religion:</label>
                            <span>{studentData?.religion?.toUpperCase() || '-'}</span>
                        </div>
                        <div className="detail-item-inline">
                            <label>Nationality:</label>
                            <span>{studentData?.nationality === 'IN' ? 'INDIAN' : studentData?.nationality || '-'}</span>
                        </div>
                        <div className="detail-item-inline">
                            <label>Current Address:</label>
                            <span>
                                {[
                                    studentData?.addressLine,
                                    studentData?.cityOrDist,
                                    studentData?.state,
                                    studentData?.pinCode
                                ].filter(Boolean).join(', ') || '-'}
                            </span>
                        </div>
                    </div>
                </div>
            </div>

            {/* Last School Details Section */}
            <div className="last-school-section">
                <h3>Last School Details</h3>
                <div className="parent-card">
                    <div className="parent-details-grid">
                        <div className="detail-section">
                            <div className="detail-item-inline">
                                <label>School Name:</label>
                                <span>{studentData?.previousSchool || '-'}</span>
                            </div>
                            <div className="detail-item-inline">
                                <label>Class Name:</label>
                                <span>{getGradeDisplayName(studentData?.previousGrade)}</span>
                            </div>
                        </div>

                        <div className="detail-section">
                            <div className="detail-item-inline">
                                <label>Admission Counsellor Name:</label>
                                <span>{studentData?.counsellorsName || '-'}</span>
                            </div>
                            <div className="detail-item-inline">
                                <label>Admission Referred by:</label>
                                <span>{studentData?.knowAboutUs || '-'}</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

/**
 * Render parent details view
 * @param {Object} studentData - Student data object
 * @returns {JSX.Element}
 */
export const renderParentDetailsView = (studentData) => {
    return (
        <div className="parent-details-view">
            <h2>Parent Details</h2>

            {/* Father Details */}
            <div className="parent-card">
                <div className="parent-details-grid">
                    <div className="detail-section">
                        <div className="detail-item-inline">
                            <label>Father Name:</label>
                            <span>{studentData?.fatherName || '-'}</span>
                        </div>
                        <div className="detail-item-inline">
                            <label>Mobile No:</label>
                            <span>{studentData?.fatherMobile || '-'}</span>
                        </div>
                        <div className="detail-item-inline">
                            <label>Email ID:</label>
                            <span>{studentData?.fatherEmail || '-'}</span>
                        </div>
                    </div>

                    <div className="detail-section">
                        <div className="detail-item-inline">
                            <label>Occupation:</label>
                            <span>{studentData?.fatherOccupation || '-'}</span>
                        </div>
                        <div className="detail-item-inline">
                            <label>Qualification:</label>
                            <span>{studentData?.fatherQualification || '-'}</span>
                        </div>
                    </div>
                </div>
            </div>

            {/* Mother Details */}
            <div className="parent-card" style={{ marginTop: '30px' }}>
                <div className="parent-details-grid">
                    <div className="detail-section">
                        <div className="detail-item-inline">
                            <label>Mother Name:</label>
                            <span>{studentData?.motherName || '-'}</span>
                        </div>
                        <div className="detail-item-inline">
                            <label>Mobile:</label>
                            <span>{studentData?.motherMobile || '-'}</span>
                        </div>
                        <div className="detail-item-inline">
                            <label>Email:</label>
                            <span>{studentData?.motherEmail || '-'}</span>
                        </div>
                    </div>

                    <div className="detail-section">
                        <div className="detail-item-inline">
                            <label>Occupation:</label>
                            <span>{studentData?.motherOccupation || '-'}</span>
                        </div>
                        <div className="detail-item-inline">
                            <label>Qualification:</label>
                            <span>{studentData?.motherQualification || '-'}</span>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

/**
 * Render guardian details view
 * @param {Object} studentData - Student data object
 * @returns {JSX.Element}
 */
export const renderGuardianDetailsView = (studentData) => {
    // Only show guardian section if guardian data exists
    const hasGuardianData = studentData?.guardianName || studentData?.guardianMobile || studentData?.guardianEmail || studentData?.guardianOccupation || studentData?.guardianQualification;
    
    if (!hasGuardianData) {
        return (
            <div className="guardian-details-view">
                <h2>Guardian Details</h2>
                <div className="parent-card">
                    <p className="no-data-message">No guardian information available</p>
                </div>
            </div>
        );
    }

    return (
        <div className="guardian-details-view">
            <h2>Guardian Details</h2>

            <div className="parent-card">
                <div className="parent-details-grid">
                    <div className="detail-section">
                        <div className="detail-item-inline">
                            <label>Name:</label>
                            <span>{studentData?.guardianName || '—'}</span>
                        </div>
                        <div className="detail-item-inline">
                            <label>Mobile:</label>
                            <span>{studentData?.guardianMobile || '—'}</span>
                        </div>
                        <div className="detail-item-inline">
                            <label>Email:</label>
                            <span>{studentData?.guardianEmail || '—'}</span>
                        </div>
                    </div>

                    <div className="detail-section">
                        <div className="detail-item-inline">
                            <label>Qualification:</label>
                            <span>{studentData?.guardianQualification || '—'}</span>
                        </div>
                        <div className="detail-item-inline">
                            <label>Occupation:</label>
                            <span>{studentData?.guardianOccupation || '—'}</span>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

/**
 * Render siblings info view
 * @param {Object} studentData - Student data object
 * @returns {JSX.Element}
 */
export const renderSiblingsInfoView = (studentData) => {
    const siblings = studentData?.siblings || [];

    return (
        <div className="siblings-info-view">
            <h2>Siblings Information</h2>

            {siblings.length > 0 ? (
                siblings.map((sibling, index) => (
                    <div key={index} className="parent-card">
                        <div className="sibling-header">
                            <h3>Sibling {index + 1}</h3>
                        </div>
                        <div className="parent-details-grid">
                            <div className="detail-section">
                                <div className="detail-item-inline">
                                    <label>Name:</label>
                                    <span>{sibling.name || '-'}</span>
                                </div>
                                <div className="detail-item-inline">
                                    <label>Gender:</label>
                                    <span>{sibling.gender === 'male' ? 'Male' : sibling.gender === 'female' ? 'Female' : '-'}</span>
                                </div>
                            </div>
                            <div className="detail-section">
                                <div className="detail-item-inline">
                                    <label>Class Studying:</label>
                                    <span>{getGradeDisplayName(sibling.classStudying)}</span>
                                </div>
                                <div className="detail-item-inline">
                                    <label>School Studying:</label>
                                    <span>{sibling.schoolStudying || '-'}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                ))
            ) : (
                <div className="no-data-message">
                    <p>No siblings information available</p>
                </div>
            )}
        </div>
    );
}

/**
 * Render transport info view
 * @param {Object} studentData - Student data object
 * @returns {JSX.Element}
 */
export const renderTransportInfoView = (studentData) => {
    const transportInfo = studentData?.transportInfo || {};
    const routeInfo = transportInfo?.routeInfo || {};
    const pickUpInfo = transportInfo?.pickUpInfo || {};
    const dropInfo = transportInfo?.dropInfo || {};
    const vehicleInfo = transportInfo?.vehicleInfo || {};
    const driverInfo = transportInfo?.driverStaffInfo?.[0] || {};

    return (
        <div className="transport-info-view">
            <h2>Transport Information</h2>

            {/* Transport Status */}
            <div className="transport-section">
                <h3>Transport Status</h3>
                <div className="details-grid">
                    <div className="detail-row">
                        <div className="detail-item transport-status-center">
                            <label>Transport Assigned:</label>
                            <span className={`status ${transportInfo?.isTransportAssigned ? 'assigned' : 'not-assigned'}`}>
                                {transportInfo?.isTransportAssigned ? 'Yes' : 'No'}
                            </span>
                        </div>
                        <div className="detail-item">
                            <label>Effective Date:</label>
                            <span>{transportInfo?.effectiveDate ? new Date(transportInfo.effectiveDate).toLocaleDateString() : '-'}</span>
                        </div>
                    </div>
                </div>
            </div>

            {transportInfo?.isTransportAssigned && (
                <>
                    {/* Route Information */}
                    <div className="transport-section">
                        <h3>Route Information</h3>
                        <div className="details-grid">
                            <div className="detail-row">
                                <div className="detail-item">
                                    <label>Route Name:</label>
                                    <span>{routeInfo?.routeName || '-'}</span>
                                </div>
                                <div className="detail-item">
                                    <label>Route Number:</label>
                                    <span>{routeInfo?.routeNo || '-'}</span>
                                </div>
                                <div className="detail-item">
                                    <label>Total Stops:</label>
                                    <span>{routeInfo?.totalStops || '-'}</span>
                                </div>
                            </div>
                            <div className="detail-row">
                                <div className="detail-item">
                                    <label>Start Point:</label>
                                    <span>{routeInfo?.startPoint || '-'}</span>
                                </div>
                                <div className="detail-item">
                                    <label>End Point:</label>
                                    <span>{routeInfo?.endPoint || '-'}</span>
                                </div>
                           {/*      <div className="detail-item">
                                    <label>Transport Fee:</label>
                                    <span>₹{routeInfo?.commonTransportFee || '-'}</span>
                                </div> */}
                            </div>
                        </div>
                    </div>

                    {/* Pickup Information */}
                    <div className="transport-section">
                        <h3>Pickup Information</h3>
                        <div className="details-grid">
                            <div className="detail-row">
                                <div className="detail-item">
                                    <label>Stop Name:</label>
                                    <span>{pickUpInfo?.stopName || '-'}</span>
                                </div>
                                <div className="detail-item">
                                    <label>Pickup Time:</label>
                                    <span>{pickUpInfo?.pickUpTime || '-'}</span>
                                </div>
                                <div className="detail-item">
                                    <label>Landmark:</label>
                                    <span>{pickUpInfo?.landmark || '-'}</span>
                                </div>
                            </div>
                            <div className="detail-row">
                                <div className="detail-item">
                                    <label>Location:</label>
                                    <span>
                                        {pickUpInfo?.latitude && pickUpInfo?.longitude
                                            ? `${pickUpInfo.latitude}, ${pickUpInfo.longitude}`
                                            : '-'}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Drop Information */}
                    <div className="transport-section">
                        <h3>Drop Information</h3>
                        <div className="details-grid">
                            <div className="detail-row">
                                <div className="detail-item">
                                    <label>Stop Name:</label>
                                    <span>{dropInfo?.stopName || '-'}</span>
                                </div>
                                <div className="detail-item">
                                    <label>Drop Time:</label>
                                    <span>{dropInfo?.dropTime || '-'}</span>
                                </div>
                                <div className="detail-item">
                                    <label>Landmark:</label>
                                    <span>{dropInfo?.landmark || '-'}</span>
                                </div>
                            </div>
                            <div className="detail-row">
                                <div className="detail-item">
                                    <label>Location:</label>
                                    <span>
                                        {dropInfo?.latitude && dropInfo?.longitude
                                            ? `${dropInfo.latitude}, ${dropInfo.longitude}`
                                            : '-'}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Vehicle Information */}
                    <div className="transport-section">
                        <h3>Vehicle Information</h3>
                        <div className="details-grid">
                            <div className="detail-row">
                                <div className="detail-item">
                                    <label>Vehicle Name:</label>
                                    <span>{vehicleInfo?.vehicleName || '-'}</span>
                                </div>
                                <div className="detail-item">
                                    <label>Registration No:</label>
                                    <span>{vehicleInfo?.vehicleRegistrationNo || '-'}</span>
                                </div>
                                <div className="detail-item">
                                    <label>Vehicle Type:</label>
                                    <span>{vehicleInfo?.vehicleType || '-'}</span>
                                </div>
                            </div>
                            <div className="detail-row">
                                <div className="detail-item">
                                    <label>Seating Capacity:</label>
                                    <span>{vehicleInfo?.seatingCapacity || '-'}</span>
                                </div>
                                <div className="detail-item">
                                    <label>Vehicle Category:</label>
                                    <span>{vehicleInfo?.vehicleCategory || '-'}</span>
                                </div>
                                <div className="detail-item">
                                    <label>Fuel Type:</label>
                                    <span>{vehicleInfo?.fuelType || '-'}</span>
                                </div>
                            </div>
                            <div className="detail-row">
                                <div className="detail-item">
                                    <label>AC/Non-AC:</label>
                                    <span>{vehicleInfo?.acNonAC ? 'AC' : 'Non-AC'}</span>
                                </div>
                                <div className="detail-item">
                                    <label>GPS Enabled:</label>
                                    <span>{vehicleInfo?.isGps ? 'Yes' : 'No'}</span>
                                </div>
                                <div className="detail-item">
                                    <label>Video Recording:</label>
                                    <span>{vehicleInfo?.videoRec ? 'Yes' : 'No'}</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Driver Information */}
                    <div className="transport-section">
                        <h3>Driver Information</h3>
                        <div className="details-grid">
                            <div className="detail-row">
                                <div className="detail-item">
                                    <label>Name:</label>
                                    <span>{driverInfo?.name || '-'}</span>
                                </div>
                                <div className="detail-item">
                                    <label>Designation:</label>
                                    <span>{driverInfo?.designation || '-'}</span>
                                </div>
                                <div className="detail-item">
                                    <label>Mobile Number:</label>
                                    <span>{driverInfo?.mobileNo || '-'}</span>
                                </div>
                            </div>
                            <div className="detail-row">
                                <div className="detail-item">
                                    <label>Emergency Contact:</label>
                                    <span>{driverInfo?.emergencyContactNo || '-'}</span>
                                </div>
                                <div className="detail-item">
                                    <label>Employment ID:</label>
                                    <span>{driverInfo?.employmentId || '-'}</span>
                                </div>
                                <div className="detail-item">
                                    <label>Employment Status:</label>
                                    <span>{driverInfo?.employmentStatus || '-'}</span>
                                </div>
                            </div>
                            <div className="detail-row">
                                <div className="detail-item">
                                    <label>License Number:</label>
                                    <span>{driverInfo?.licenceNo || '-'}</span>
                                </div>
                                <div className="detail-item">
                                    <label>License Type:</label>
                                    <span>{driverInfo?.licenceType || '-'}</span>
                                </div>
                                <div className="detail-item">
                                    <label>License Expiry:</label>
                                    <span>{driverInfo?.licenceExpiryDate ? new Date(driverInfo.licenceExpiryDate).toLocaleDateString() : '-'}</span>
                                </div>
                            </div>
                            <div className="detail-row">
                                <div className="detail-item">
                                    <label>Date of Joining:</label>
                                    <span>{driverInfo?.dateOfJoining ? new Date(driverInfo.dateOfJoining).toLocaleDateString() : '-'}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </>
            )}

            {!transportInfo?.isTransportAssigned && (
                <div className="no-transport-message">
                    <p>No transport information available for this student.</p>
                </div>
            )}
        </div>
    );
}
